// Browser-direct resumable upload to YouTube (ADR-0004).
//
// The bytes go from this tab straight to Google [C030]. Nothing proxies them,
// so the only size limit is YouTube's own 256 GB [C025].
//
// Every failure here is logged to app_logs under the `youtube_upload` flow,
// with the step that failed and enough numbers to tell the cases apart:
// "Network error during upload" at 3% on a 4 GB file is a different problem
// from the same message at 99%.

import { callFunction } from "./supabase";
import { logError, logEvent, newRunId, type LogFields } from "./logger";

interface SessionResponse {
  upload_uri: string;
  byte_size: number;
  mime_type: string;
  /** What we asked YouTube for, taken from the post. */
  privacy_status: string;
  /** The origin Google bound the session's CORS to; null means none was set. */
  cors_origin?: string | null;
}

interface StatusResponse {
  offset: number;
  completed: boolean;
  video_id: string | null;
  privacy_status: string | null;
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percent: number;
}

/** Carries the transport-level facts a bare Error loses. */
class UploadError extends Error {
  constructor(message: string, readonly detail: Record<string, unknown> = {}) {
    super(message);
    this.name = "UploadError";
  }
}

export async function uploadToYouTube(
  deliveryId: string,
  file: File,
  onProgress: (p: UploadProgress) => void,
  meta: { postId?: string | null; workspaceId?: string | null; runId?: string } = {},
): Promise<{ videoId: string; privacyStatus: string; requestedPrivacyStatus: string }> {
  const runId = meta.runId ?? newRunId();
  const startedAt = Date.now();

  // Shared by every log line from this upload, so one query returns the whole story.
  const where: LogFields = {
    runId,
    deliveryId,
    postId: meta.postId ?? null,
    workspaceId: meta.workspaceId ?? null,
    platform: "youtube",
  };
  const fileFacts = {
    byte_size: file.size,
    mime_type: file.type || null,
    filename_ext: file.name.split(".").pop() ?? null,
  };

  let session: SessionResponse;
  try {
    session = await callFunction<SessionResponse>("youtube-upload-session", {
      action: "create",
      delivery_id: deliveryId,
      run_id: runId,
    });
  } catch (e) {
    await logError("youtube_upload", e, {
      ...where,
      step: "create_session",
      context: fileFacts,
    });
    throw e;
  }

  // If a previous attempt got partway, resume from where Google has us.
  const { offset } = await callFunction<StatusResponse>("youtube-upload-session", {
    action: "status",
    delivery_id: deliveryId,
    run_id: runId,
  }).catch(async (e) => {
    // Not fatal — a failed offset query just means starting from zero — but it
    // is the reason a resume silently became a full re-upload.
    await logError("youtube_upload", e, {
      ...where,
      step: "query_offset",
      level: "warn",
      context: fileFacts,
    });
    return { offset: 0, completed: false, video_id: null, privacy_status: null };
  });

  if (offset > 0) {
    void logEvent("youtube_upload", `Resuming upload at ${offset} bytes`, {
      ...where,
      step: "resume",
      context: { ...fileFacts, offset },
    });
  }

  let result: { id: string; privacyStatus: string };
  try {
    result = await putBytes(session.upload_uri, file, offset, onProgress);
  } catch (e) {
    const detail = e instanceof UploadError ? e.detail : {};
    await logError("youtube_upload", e, {
      ...where,
      step: "put_bytes",
      context: {
        ...fileFacts,
        ...detail,
        start_offset: offset,
        elapsed_ms: Date.now() - startedAt,
        online: typeof navigator === "undefined" ? null : navigator.onLine,
        cors_origin: session.cors_origin ?? null,
        // status 0 with bytes on the wire is the CORS signature: Google
        // answered, the browser refused to let us read the answer.
        likely_cors: detail.http_status === null && (detail.bytes_sent as number) > 0,
      },
    });

    // The transfer may have finished even though the browser could not read
    // the response. Ask Google before telling the user it failed — the video
    // is already on their channel, and re-uploading would duplicate it.
    const recovered = await reconcile(deliveryId, runId, where);
    if (recovered) {
      onProgress({ loaded: file.size, total: file.size, percent: 100 });
      result = recovered;
    } else {
      throw e;
    }
  }

  try {
    await callFunction("youtube-upload-session", {
      action: "complete",
      delivery_id: deliveryId,
      video_id: result.id,
      privacy_status: result.privacyStatus,
      run_id: runId,
    });
  } catch (e) {
    // The video is on YouTube; only our record of it failed. That distinction
    // is the whole reason this is logged separately from put_bytes.
    await logError("youtube_upload", e, {
      ...where,
      step: "complete",
      level: "fatal",
      context: { ...fileFacts, video_id: result.id, uploaded: true },
    });
    throw e;
  }

  if (result.privacyStatus !== session.privacy_status) {
    void logEvent(
      "youtube_upload",
      `YouTube applied ${result.privacyStatus}, ${session.privacy_status} was requested`,
      {
        ...where,
        step: "privacy_mismatch",
        level: "warn",
        context: {
          requested: session.privacy_status,
          applied: result.privacyStatus,
          video_id: result.id,
        },
      },
    );
  }

  return {
    videoId: result.id,
    privacyStatus: result.privacyStatus,
    requestedPrivacyStatus: session.privacy_status,
  };
}

/**
 * Asks the server what Google actually has. Returns the video when the upload
 * turns out to have completed, null when it genuinely did not.
 */
async function reconcile(
  deliveryId: string,
  runId: string,
  where: LogFields,
): Promise<{ id: string; privacyStatus: string } | null> {
  try {
    const status = await callFunction<StatusResponse>("youtube-upload-session", {
      action: "status",
      delivery_id: deliveryId,
      run_id: runId,
    });
    if (!status.completed || !status.video_id) return null;

    void logEvent("youtube_upload", "Upload had completed despite the transport error", {
      ...where,
      step: "reconcile",
      level: "warn",
      context: { video_id: status.video_id },
    });

    return { id: status.video_id, privacyStatus: status.privacy_status ?? "unknown" };
  } catch (e) {
    await logError("youtube_upload", e, { ...where, step: "reconcile", level: "warn" });
    return null;
  }
}

/** XHR rather than fetch: fetch still has no upload progress event. */
function putBytes(
  uploadUri: string,
  file: File,
  offset: number,
  onProgress: (p: UploadProgress) => void,
): Promise<{ id: string; privacyStatus: string }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const body = offset > 0 ? file.slice(offset) : file;
    // How far the transfer actually got. The single most useful number when a
    // network error is reported, and the one the old Error carried none of.
    let lastLoaded = offset;

    xhr.open("PUT", uploadUri, true);
    xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
    if (offset > 0) {
      // Resume [C030].
      xhr.setRequestHeader("Content-Range", `bytes ${offset}-${file.size - 1}/${file.size}`);
    }

    xhr.upload.onprogress = (e) => {
      if (!e.lengthComputable) return;
      const loaded = offset + e.loaded;
      lastLoaded = loaded;
      onProgress({ loaded, total: file.size, percent: Math.round((loaded / file.size) * 100) });
    };

    const transfer = () => ({
      bytes_sent: lastLoaded,
      bytes_total: file.size,
      percent: file.size ? Math.round((lastLoaded / file.size) * 100) : 0,
      http_status: xhr.status || null,
    });

    xhr.onload = () => {
      if (xhr.status === 200 || xhr.status === 201) {
        try {
          const payload = JSON.parse(xhr.responseText);
          if (!payload.id) {
            return reject(new UploadError("YouTube returned no video id", transfer()));
          }
          resolve({ id: payload.id, privacyStatus: payload.status?.privacyStatus ?? "unknown" });
        } catch {
          reject(new UploadError("Could not read YouTube's response", {
            ...transfer(),
            response_head: xhr.responseText?.slice(0, 300) ?? null,
          }));
        }
      } else if (xhr.status === 308) {
        reject(new UploadError("Upload incomplete — reopen this post to resume", transfer()));
      } else {
        reject(new UploadError(
          `YouTube rejected the upload (${xhr.status}): ${xhr.responseText}`,
          { ...transfer(), response_head: xhr.responseText?.slice(0, 500) ?? null },
        ));
      }
    };

    // The reported failure. XHR gives no cause for onerror by design, so the
    // context around it — how far it got, how long it took, whether the tab
    // was online — is all the diagnosis there will ever be.
    xhr.onerror = () => reject(new UploadError("Network error during upload", transfer()));
    xhr.ontimeout = () => reject(new UploadError("Upload timed out", transfer()));
    xhr.onabort = () => reject(new UploadError("Upload cancelled", transfer()));
    xhr.send(body);
  });
}
